import type { AllIconType, ColorPickerColorType, GenderType, MaterialIconType, SystemColorType, TeachersResourceType, YearGroupColorType } from './types';
import type {
  Locales,
  ReportsFilterOptionsType,
  ReportsSectionsType,
  ReportsTableViewType,
  SchoolReportsSectionsType,
} from '../pages/types';

export const ICON_SIZE = 30;
export const EMPTY_VALUE = '—';
export const SELECTOR_WIDTH = 270;

export const PROGRESSIONS_MAX_NUM = 3;

export const LAST_WEEKS = 8;

export const BUDDHIST_YEARS = 543;

export const EMPTY_CELL_CONTENTS = [ '', EMPTY_VALUE, 'None', null, undefined ];

type GlobalIconType =
  | 'toggle_off'
  | 'toggle_on'
  | 'sort_up'
  | 'sort_down'
  | 'first'
  | 'previous'
  | 'next'
  | 'last'
  | 'see_on'
  | 'see_off'
  | 'menu'
  | 'search'
  | 'close'
  | 'confirm'
  | 'collapse_off'
  | 'collapse_on'
  | 'message'
  | 'image'
  | 'delete'
  | 'download'
  | 'upload'
  | 'checkbox'
  | 'checkbox_indeterminate'
  | 'radio'
  | 'calendar'
  | 'open_modal'
  | 'logout'
  | 'history'
  | 'play'
  | 'reload'
  | 'refresh'
  | 'filter'
  | 'settings'
  | 'tick'
  | 'cross'
  | 'help'
  | 'parent_link_tick'
  | 'edit'
  | 'add'
  | 'save'
  | 'block'
  | 'lightbulb'
  | 'roster_management'
  | SystemColorType
  | Extract<RouteType, 'home' | 'topic_focus' | 'lesson_history' | 'reports' | 'school_reports' | 'logout'>;
export const GLOBAL_ICONS: Record<GlobalIconType, MaterialIconType> = {
  toggle_off: 'KeyboardArrowDown',
  toggle_on: 'KeyboardArrowUp',
  sort_up: 'ArrowDropUp',
  sort_down: 'ArrowDropDown',
  first: 'FirstPage',
  previous: 'NavigateBefore',
  next: 'NavigateNext',
  last: 'LastPage',
  see_on: 'Visibility',
  see_off: 'VisibilityOff',
  collapse_off: 'AddRounded',
  collapse_on: 'RemoveRounded',
  menu: 'Menu',
  search: 'Search',
  close: 'Close',
  confirm: 'Check',
  message: 'Message',
  image: 'Image',
  delete: 'DeleteForever',
  info: 'Info',
  error: 'Warning',
  warning: 'Warning',
  success: 'CheckCircle',
  download: 'GetApp',
  upload: 'Publish',
  checkbox: 'Check',
  checkbox_indeterminate: 'Remove',
  radio: 'FiberManualRecord',
  calendar: 'CalendarToday',
  open_modal: 'OpenInNew',
  topic_focus: 'Launch',
  lesson_history: 'History',
  reports: 'SupervisorAccount',
  school_reports: 'LocationCity',
  logout: 'ExitToApp',
  history: 'History',
  play: 'PlayCircleOutline',
  refresh: 'RefreshOutlined',
  reload: 'Autorenew',
  filter: 'HighlightTwoTone',
  settings: 'Settings',
  tick: 'CheckCircleTwoTone',
  cross: 'CancelTwoTone',
  help: 'HelpTwoTone',
  parent_link_tick: 'Done',
  home: 'Home',
  edit: 'Edit',
  add: 'Add',
  save: 'Save',
  block: 'Block',
  lightbulb: 'EmojiObjects', //Todo: use correct lightbulb icon
  roster_management: 'SupervisedUserCircle',
};

const QualityIcon: MaterialIconType = 'Timer';
const ProgressionIcon: MaterialIconType = 'PlusOne';
const TopicProgressIcon: MaterialIconType = 'Folder';
const StudentGroupsIcon: MaterialIconType = 'BarChart';

export const FILTER_ICONS: Record<ReportsFilterOptionsType, MaterialIconType | 'Student3d'> = {
  student: 'Student3d',
  quality: QualityIcon,
  progression: ProgressionIcon,
  gender: 'PeopleAlt',
  tag: 'Label',
};

export const REPORT_SECTIONS_ICONS: Record<ReportsSectionsType, AllIconType> = {
  maths_age: 'Student',
  maths_age_improvements: 'TrendingUp',
  progressions: ProgressionIcon,
  quality: QualityIcon,
  consistency: StudentGroupsIcon,
  student_status: StudentGroupsIcon,
  topic_progress: TopicProgressIcon,
};

export const SCHOOL_REPORT_SECTIONS_ICONS: Record<SchoolReportsSectionsType, AllIconType> = {
  maths_age_delta: 'CompareArrows',
  maths_age_improvements: 'TrendingUp',
  progressions: ProgressionIcon,
  quality: QualityIcon,
  consistency: StudentGroupsIcon,
  initial_assessment: 'Assessment',
  topic_progress: TopicProgressIcon,
};

export const REPORT_TABLES_ICONS: Record<ReportsTableViewType, AllIconType> = {
  report_table: 'TableChart',
  topic_progress_table: TopicProgressIcon,
};

type ZIndexType =
  | 'max'
  | 'level_1'
  | 'level_2'
  | 'level_3'
  | 'level_4'
  | 'level_5'
  | 'level_6'
  | 'level_7'
  | 'level_8'
  | 'level_9'
  | 'level_10';
export const Z_INDEXES: Record<ZIndexType, number> = {
  max: 9999,
  level_1: 100,
  level_2: 200,
  level_3: 300,
  level_4: 400,
  level_5: 500,
  level_6: 600,
  level_7: 700,
  level_8: 800,
  level_9: 900,
  level_10: 1000,
};

export type RouteType = keyof typeof ROUTES;

export const ROUTES = {
  home: '/',
  reports: '/reports',
  school_reports: '/school-reports',
  lesson_history: '/history',
  topic_focus: '/topic-focus',
  messages: '/messages',
  reset_assessments: '/reset-assessments',
  roster_management: '/roster-management',
  password_initialisation: '/password',
  logout: '/logout',
  pdf: '/build-pdf',
  parent_email: '/parent-email',
  teachers_resource: `https://${process.env.REACT_APP_HOSTNAME_RESOURCE}`,
  certificate: `https://${process.env.REACT_APP_HOSTNAME_RESOURCE}/certificate`,
  curriculum_report: `https://${process.env.REACT_APP_HOSTNAME_SERVICE_COMBINED}/schools/reports/curriculum`,
  superAdmin: '/admin',
};

export const ROSTER_ROUTES = {
  student: '/student',
  staff: '/staff',
  class: '/class',
  tags: '/tags',
  upload: '/upload',
  parents: '/parents',
  upload_legacy: '/upload-legacy',
  student_upload:'/student-upload',
  tidy: '/tidy',
};

export const PARENT_EMAIL_ROUTES = {
  signup: '/signup',
  consent: '/consent',
  unsubscribe: '/unsubscribe',
};

export const ADMIN_ROUTES = {
  login: `${ROUTES.superAdmin}`,
  schools: '/schools',
  school: '/school',
  implementation: {
    home: '/implementation',
    impactReports: '/impact-reports',
    weeklyLeadersEmails: '/weekly-leaders-emails',
  },
};

export const STATUS: Record<string, string> = {
  IDLE: 'idle',
  LOADING: 'loading',
  SENDING: 'sending',
  SUCCESS: 'success',
  ERROR: 'error',
};

export const QUERIES_IDS = {
  CLASSES: 'CLASSES',
  TOPIC_FOCUS_REPORT: 'TOPIC_FOCUS_REPORT',
  TOPIC_STUDENTS: 'TOPIC_STUDENTS',
  GROUP_REPORT: 'GROUP_REPORT',
  GROUP_MESSAGES: 'GROUP_MESSAGES',
  HOME_GROUP_REPORT: 'HOME_GROUP_REPORT',
  ACADEMIC_YEAR_START_DATE: 'ACADEMIC_YEAR_START_DATE',
  GROUP_REPORT_METRICS: 'GROUP_REPORT_METRICS',
  SCHOOL_REPORT: 'SCHOOL_REPORT',
  SCHOOL_REPORT_JOB: 'SCHOOL_REPORT_JOB',
  SUBSCRIBED_PRODUCTS: 'SUBSCRIBED_PRODUCTS',
  GROUP_STUDENTS: 'GROUP_STUDENTS',
  LESSON_HISTORY: 'LESSON_HISTORY',
  STAFF: 'STAFF',
  ROSTER_MANAGEMENT: {
    ASSESSMENT_DELTA: 'ROSTER_MANAGEMENT_ASSESSMENT_DELTA',
    STUDENTS: 'ROSTER_MANAGEMENT_STUDENTS',
    STUDENTS_STATISTICS: 'ROSTER_MANAGEMENT_STUDENTS_STATISTICS',
    TAGS: 'ROSTER_MANAGEMENT_TAGS',
    RECOVERY_EMAIL: 'ROSTER_MANAGEMENT_RECOVERY_EMAIL',
    CLASSES: 'ROSTER_MANAGEMENT_CLASSES',
    GUARDIANS: 'ROSTER_MANAGEMENT_GUARDIANS',
  },
  YEAR_GROUPS: 'YEAR_GROUPS',
  STUDENT_TO_BE_EDITED: 'STUDENT_TO_BE_EDITED',
  PASSWORD_INITIALISATION: 'PASSWORD_INITIALISATION',
  UPLOAD: 'UPLOAD',
  STUDENTS_COUNT: 'STUDENTS_COUNT',
  DEFAULT_YEAR_GROUPS: 'DEFAULT_YEAR_GROUPS',
  PARENTS: 'PARENTS',
  CURRENT_LESSON: 'CURRENT_LESSON',

  ADMIN: {
    GET_SCHOOLS: 'GET_SCHOOLS',
    GET_GOALS: 'GET_GOALS',
    GET_WEEKLY_LEADERS_EMAILS: 'GET_WEEKLY_LEADERS_EMAILS',
    GET_SCHOOL_TEACHERS: 'GET_SCHOOL_TEACHERS',
    GET_SCHOOL_CLASSES: 'GET_SCHOOL_CLASSES',
    GET_SCHOOL_TAGS: 'GET_SCHOOL_TAGS',
    GET_MAILCHIMP_RENDERED_TEMPLATE: 'GET_MAILCHIMP_RENDERED_TEMPLATE',
    GET_SCHOOL_WEEKLY_LEADERS_EMAIL_PREVIEW_DYNAMIC_DATA: 'GET_SCHOOL_WEEKLY_LEADERS_EMAIL_PREVIEW_DYNAMIC_DATA',
    GET_SCHOOL_IMPLEMENTATION_PLAN: 'GET_SCHOOL_IMPLEMENTATION_PLAN',
    GET_IMPACT_REPORT_TEMPLATES: 'GET_IMPACT_REPORT_TEMPLATES',
    IMPACT_REPORT_JOB: 'IMPACT_REPORT_JOB',
    GET_SCHOOL_IMPACT_REPORT_CONFIGURATION: 'GET_SCHOOL_IMPACT_REPORT_CONFIGURATION',
  },
};

export const MUTATIONS_IDS = {
  STUDENTS_ACTIONS: 'STUDENTS_ACTIONS',
  ROSTER_MANAGEMENT: {
    UPLOAD_GENERIC_FILE: 'ROSTER_MANAGEMENT_UPLOAD_GENERIC_FILE',
  },
};

export const YEAR_GROUP_COLORS: Record<number | 'NO_YEAR_GROUP', YearGroupColorType> = {
  1: 'preschool',
  2: 'foundation',
  3: 'year1',
  4: 'year2',
  5: 'year3',
  6: 'year4',
  7: 'year5',
  8: 'year6',
  9: 'year7',
  10: 'year8',
  11: 'year9',
  12: 'year10',
  13: 'year11',
  'NO_YEAR_GROUP': 'year_none',
};

export const COLOR_PICKER_COLORS: Record<number, ColorPickerColorType> = {
  0: 'preschool',
  1: 'foundation',
  2: 'year1',
  3: 'year2',
  4: 'year3',
  5: 'year4',
  6: 'year5',
  7: 'year6',
  8: 'year7',
  9: 'year8',
  10: 'year9',
  11: 'year10',
  12: 'year11',
  13: 'year_none',
  14: 'color15',
  15: 'color16',
  16: 'color17',
  17: 'color18',
  18: 'color19',
  19: 'color20',
};

export const TEACHERS_RESOURCES: Record<Locales, TeachersResourceType[] | null> = {
  'gb': [ { locale: 'en-gb' } ],
  'us': [ { locale: 'en-us' }, { locale: 'es-mx' } ],
  'ke': [ { locale: 'en-ke', flag: 'en-gb' }, { locale: 'sw-ke' } ],
  'nz': [ { locale: 'en-nz' } ],
  'mx': [ { locale: 'en-us' }, { locale: 'es-mx' } ],
  'cd': [ { locale: 'fr-cd' } ],
  'th': [ { locale: 'en-gb' }, { locale: 'th-th' } ],
};

export type Curriculums = 'england-2014' | 'white-rose' | 'common-core' | 'teks' | 'kenya' | 'drc' | 'tahurangi' | 'te-mataiaho';

export const CURRICULUM_REPORTS: Record<Locales, Curriculums[] | null> = {
  'gb': [ 'england-2014', 'white-rose' ],
  'us': [ 'common-core', 'teks' ],
  'ke': [ 'kenya' ],
  'nz': [ 'tahurangi', 'te-mataiaho' ],
  'mx': null,
  'cd': [ 'drc' ],
  'th': null,
};

export const PRODUCT_FRUITS_TOURS = {
  noStudents: 52192, // These IDs are from ProductFruits. Do not change!
  noTeachers: 52248,
  students: 52657,
  staff: 52645,
  lifeRing: 52876,
  teacherFeed: 49350,
  loginDetails: 88552,
};

export const genders: Record<number, { abbreviation: 'M' | 'F' | 'O', name: GenderType }> = {
  1: {
    abbreviation: 'M',
    name: 'male',
  },
  2: {
    abbreviation: 'F',
    name: 'female',
  },
  3: {
    abbreviation: 'O',
    name: 'other',
  },
};

export const ADMIN_BEARER_NAME = 'adminBearer';
